"use client";
// РУКА 2. Строка поиска в шапке с подсказками. Печатаешь от двух букв — под строкой до 6 товаров;
// стрелки ↑↓ выбирают, Enter открывает товар (или страницу всех результатов), Esc закрывает.
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { suggest, type Suggestion } from "./search";
import { Icon } from "./look/icons";

const rub = new Intl.NumberFormat("ru-RU");

export function SearchBox() {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();
  const [q, setQ] = useState(pathname === "/search" ? (sp.get("q") ?? "") : "");
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loaded, setLoaded] = useState("");
  const box = useRef<HTMLDivElement>(null);

  // Подсказки — через 200 мс после последней буквы, чтобы не дёргать сервер на каждое нажатие.
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) return;
    let alive = true;
    const t = setTimeout(async () => {
      const r = await suggest(query);
      if (!alive) return;
      setItems(r);
      setActive(-1);
      setLoaded(query);
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  // Клик мимо — закрыть.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const query = q.trim();
  const show = open && query.length >= 2 && loaded === query;

  const goAll = () => {
    if (query.length < 2) return;
    setOpen(false);
    router.push(`/search?q=${encodeURIComponent(query)}`);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Enter" && show && active >= 0 && items[active]) {
      e.preventDefault();
      setOpen(false);
      router.push(`/product/${items[active].id}`);
    }
  };

  // Вид — как в макете (Р-020): строка с лупой, «По фото», «Голосом» и «Найти»; подсказки — списком под строкой.
  return (
    <div className="srch" ref={box}>
      <form
        role="search"
        className="sbox"
        onSubmit={(e) => {
          e.preventDefault();
          goAll();
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4-4" />
        </svg>
        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Название, артикул или бренд"
          aria-label="Поиск по каталогу"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={show}
          aria-controls="search-suggest"
          autoComplete="off"
        />
        {/* подбор по фото и голосом — неделя 5 (критерий 2.12); пока ведут на страницу «как будет» */}
        <Link className="sic sicl ph" href="/podbor" data-tip="Найти по фото" aria-label="Найти по фото">
          <Icon name="cam" />
          <span>По фото</span>
        </Link>
        <Link className="sic sicl vc" href="/podbor" data-tip="Сказать голосом" aria-label="Сказать голосом">
          <Icon name="mic" />
          <span>Голосом</span>
        </Link>
        <button type="submit" className="sgo">
          Найти
        </button>
      </form>
      {show ? (
        <div id="search-suggest" className="sugg">
          {items.length === 0 ? (
            <div className="it">По запросу «{query}» ничего не нашли</div>
          ) : (
            <ul role="listbox" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {items.map((s, k) => (
                <li key={s.id} role="option" aria-selected={k === active}>
                  <Link
                    href={`/product/${s.id}`}
                    onClick={() => setOpen(false)}
                    onMouseEnter={() => setActive(k)}
                    className={`it ${k === active ? "hl" : ""}`}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <b style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</b>
                      <span className="xs">
                        арт. {s.code}
                        {s.inStock ? "" : " · нет в наличии"}
                      </span>
                    </span>
                    <b className="mono" style={{ whiteSpace: "nowrap" }}>
                      {rub.format(s.price)} ₽
                    </b>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
